// frontend/app/components/lessonPlans/LessonAssetCompanion.js
//
// Keeps the lesson on screen while its 3D scene or video plays.
//
// The Unity iframe must not move in the React tree — remounting it costs a
// ~1 minute cold reload — so rather than nesting the stage inside the lesson
// layout, the lesson step docks beside the stage. Same pattern as
// `JourneyAssetCompanion`, but rendering real lesson blocks so a check can be
// answered while looking at the model.

import React, { useMemo } from "react";
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import LessonBlockRenderer from "./LessonBlockRenderer";
import { ActionButton, Pill } from "./blocks/LessonBlockShell";
import {
  getLessonColors,
  getModeMeta,
  getSectionMeta,
  getStepSection,
} from "./lessonPlanTheme";
import { groupStepsBySection, stepDisplayTitle, stepProgress } from "./lessonPlanRules";
import useLessonBlockHandlers from "./useLessonBlockHandlers";

const LessonAssetCompanion = ({
  lesson,
  step,
  stepIndex = 0,
  totalSteps = 0,
  activeBlockId = null,
  sessionState = {},
  onSessionStateChange,
  readOnly = false,
  launchingBlockId = null,
  onLaunchBlock,
  onAsk,
  onBack,
  onStepChange,
  isDarkMode = false,
  mode = "teacher",
  t,
}) => {
  const translate = t || ((value) => value);
  const colors = getLessonColors(isDarkMode);
  // The section the step belongs to (phases are a hidden key, never shown).
  const phase = getSectionMeta(getStepSection(step));
  const sectionGroup = groupStepsBySection(lesson?.steps).find((group) =>
    group.steps.some((entry) => entry.step === step || entry.step?.step_key === step?.step_key)
  );
  const displayTitle = stepDisplayTitle(
    step,
    Math.max(
      0,
      sectionGroup
        ? sectionGroup.steps.findIndex((entry) => entry.step?.step_key === step?.step_key)
        : 0
    ),
    sectionGroup ? sectionGroup.steps.length : 1,
    translate
  );
  const modeMeta = getModeMeta(step?.instructional_mode);

  const { handleAnswer, handleCheck, handleLaunch } = useLessonBlockHandlers({
    sessionState,
    onSessionStateChange,
    readOnly,
    onLaunchBlock,
  });

  const conceptLabels = useMemo(() => {
    const map = {};
    (lesson?.concepts || []).forEach((concept) => {
      map[concept.concept_key] = concept.label;
    });
    return map;
  }, [lesson]);

  const progress = stepProgress(step, sessionState);
  const blocks = Array.isArray(step?.blocks) ? step.blocks : [];

  if (!step) return null;

  return (
    <View
      style={[
        styles.panel,
        { backgroundColor: colors.panelBg, borderColor: colors.border },
      ]}
    >
      {/* Header ------------------------------------------------------- */}
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <TouchableOpacity
          onPress={onBack}
          accessibilityRole="button"
          accessibilityLabel={translate("Back to lesson")}
          style={[styles.backButton, { borderColor: colors.border }]}
        >
          <MaterialCommunityIcons
            name="arrow-left"
            size={16}
            color={colors.textSecondary}
          />
          <Text style={[styles.backText, { color: colors.textSecondary }]}>
            {translate("Back to lesson")}
          </Text>
        </TouchableOpacity>

        <Text style={[styles.eyebrow, { color: phase.accent }]} numberOfLines={1}>
          {translate("Step")} {stepIndex + 1} {translate("of")} {totalSteps} ·{" "}
          {translate(phase.label)}
        </Text>
        <Text style={[styles.title, { color: colors.textPrimary }]} numberOfLines={2}>
          {displayTitle}
        </Text>

        <View style={styles.metaRow}>
          {modeMeta ? (
            <Pill
              colors={colors}
              label={modeMeta.label}
              accent={modeMeta.accent}
              background={`${modeMeta.accent}18`}
            />
          ) : null}
          {step.estimated_minutes ? (
            <Pill
              colors={colors}
              label={`${step.estimated_minutes} ${translate("min")}`}
              icon="clock-outline"
            />
          ) : null}
        </View>

        {progress > 0 ? (
          <View style={[styles.progressTrack, { backgroundColor: colors.border }]}>
            <View
              style={[
                styles.progressFill,
                {
                  backgroundColor: phase.accent,
                  width: `${Math.round(progress * 100)}%`,
                },
              ]}
            />
          </View>
        ) : null}
      </View>

      {/* Step content ------------------------------------------------- */}
      <ScrollView
        style={styles.body}
        contentContainerStyle={styles.bodyContent}
        showsVerticalScrollIndicator={false}
      >
        {mode === "teacher" && step.teacher_guidance ? (
          <View
            style={[
              styles.guidance,
              { backgroundColor: colors.aiCardBg, borderColor: colors.aiCardBorder },
            ]}
          >
            <Text style={[styles.guidanceLabel, { color: colors.accentPrimary }]}>
              {translate("What you do")}
            </Text>
            <Text style={[styles.guidanceText, { color: colors.textPrimary }]}>
              {step.teacher_guidance}
            </Text>
          </View>
        ) : null}

        {blocks.map((block) => (
          <View
            key={block.id || `${step.step_key}-${block.type}`}
            style={
              // Mark the asset currently filling the stage.
              block.id && block.id === activeBlockId
                ? [styles.activeBlock, { borderColor: phase.accent }]
                : null
            }
          >
            <LessonBlockRenderer
              block={block}
              colors={colors}
              isDarkMode={isDarkMode}
              mode={mode}
              sessionState={sessionState}
              conceptLabels={conceptLabels}
              readOnly={readOnly}
              launchingBlockId={launchingBlockId}
              onAnswer={handleAnswer}
              onCheck={handleCheck}
              onLaunch={handleLaunch}
              onAsk={onAsk}
              t={t}
            />
          </View>
        ))}
      </ScrollView>

      {/* Step navigation ---------------------------------------------- */}
      <View style={[styles.footer, { borderTopColor: colors.border }]}>
        <ActionButton
          colors={colors}
          label={translate("Back")}
          icon="arrow-left"
          variant="outline"
          disabled={stepIndex <= 0}
          onPress={() => onStepChange && onStepChange(stepIndex - 1)}
        />
        <View style={{ flex: 1 }} />
        <ActionButton
          colors={colors}
          label={translate("Next")}
          icon="arrow-right"
          disabled={stepIndex >= totalSteps - 1}
          onPress={() => onStepChange && onStepChange(stepIndex + 1)}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  panel: {
    flex: 1,
    borderRadius: 16,
    borderWidth: 1,
    overflow: "hidden",
  },
  header: { paddingHorizontal: 14, paddingVertical: 12, borderBottomWidth: 1 },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginBottom: 10,
    gap: 5,
  },
  backText: { fontSize: 11, fontWeight: "700", marginLeft: 5 },
  eyebrow: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.6,
    textTransform: "uppercase",
  },
  title: { fontSize: 16, fontWeight: "800", marginTop: 3 },
  metaRow: { flexDirection: "row", flexWrap: "wrap", gap: 6, marginTop: 8 },
  progressTrack: { height: 3, borderRadius: 2, marginTop: 10, overflow: "hidden" },
  progressFill: { height: 3, borderRadius: 2 },

  body: { flex: 1 },
  bodyContent: { padding: 14 },
  activeBlock: {
    borderLeftWidth: 3,
    borderRadius: 4,
    paddingLeft: 8,
    marginLeft: -11,
  },

  guidance: { borderRadius: 12, borderWidth: 1, padding: 12, marginBottom: 12 },
  guidanceLabel: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.6,
    textTransform: "uppercase",
    marginBottom: 4,
  },
  guidanceText: { fontSize: 13, lineHeight: 20 },

  footer: {
    flexDirection: "row",
    alignItems: "center",
    borderTopWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 8,
  },
});

export default LessonAssetCompanion;
